import { useState, useRef, useEffect } from 'react';
import { Card, Col, Container, Row, Modal, Form, Button } from 'react-bootstrap';
import '../candidateRightContentPane/candidateFileContentPanel.css';
import _ from 'lodash';
import ClientUserService from '../../../services/client-services/clientUserService';
import pdfIcon from '../../../assets/images/Ic_Pdf.png';
import DownloadCV from '../../../assets/images/ic_download.png';
import { CandidateService } from '../../../services/candidateService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faMinusCircle, faDownload, faInfoCircle, faPlusCircle, faUpload, faFile, faFilePdf, faFileWord, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { CandidatePostLoginService } from '../../../services/candidatePostLoginService';

const CandidateFileContentsPanel = ({ title, fileType, fileList = [], onUploadSuccess, tooltipText }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localFiles, setLocalFiles] = useState(fileList || []);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const inputRef = useRef(null);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  const downloadFile = (resumePath, fileName) => {
    if (!resumePath) {
      console.error("Missing file path");
      return;
    }
    ClientUserService.downloadFile(encodeURI(resumePath))
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error(err));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExts = ['pdf', 'doc', 'docx'];
    const fileExt = file.name.split('.').pop()?.toLowerCase();

    const isValidType = allowedTypes.includes(file.type) || allowedExts.includes(fileExt);
    if (!isValidType) {
      setErrorMessage('Upload Failed. Please upload a supported file format (pdf, doc, docx).');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    CandidatePostLoginService.uploadCandidateFile(
      formData,
      fileType,
      (res) => {
        setUploading(false);
        const fileData = res?.data || res;
        if (fileData?.path && fileData?.originalName) {
          const updatedFiles = fileType === 'ADD_INFO' ? [fileData] : [...localFiles, fileData];

          setLocalFiles(updatedFiles);
          onUploadSuccess && onUploadSuccess(fileData);
          setErrorMessage('Upload Successful!');
        } else {
          console.error("Invalid upload response:", res);
        }
      },
      () => setUploading(false)
    );
  };

  const truncateFileName = (name) => {
    return name.length > 10 ? `${name.slice(0, 10)}...` : name;
  };

  const renderNoFilesMessage = () => (
    <Row><Col className="text-muted"></Col></Row>
  );

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      setUploading(true);
      await CandidatePostLoginService.deleteCandidateFile(
        fileToDelete.path,
        fileType,
        () => { },
        () => { }
      );

      const updatedFiles = localFiles.filter(f => f.path !== fileToDelete.path);
      setLocalFiles(updatedFiles);
      setFileToDelete(null);
      setShowDeleteModal(false);
      setErrorMessage('File deleted successfully');
    } catch (err) {
      console.error('Delete failed:', err);
      setErrorMessage('File deletion failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return faFilePdf;
    if (ext === 'doc' || ext === 'docx') return faFileWord;
    return faFilePdf;
  };
  return (
    <Card className="candidateFileContentsPanel mt-2">
      {errorMessage && (
        <div className="custom-alert-overlay">
          <div className="custom-alert-box">
            <div className="custom-alert-content">
              <p>{errorMessage}</p>
              <button className="custom-alert-button" onClick={() => setErrorMessage('')}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center file-modal-body">
          <p>Are you sure you want to delete this file?</p>
          <div className="d-flex justify-content-center gap-3">
            <Button onClick={() => setShowDeleteModal(false)} className='file-buttons-cancel'>Cancel</Button>
            <Button onClick={confirmDelete} className='file-buttons-delete'>Delete</Button>
          </div>
        </Modal.Body>
      </Modal>
      <Card.Body>
        <Container>
          <Row className="align-items-center justify-content-between flex-nowrap">
            <Col className="d-flex align-items-center gap-1 flex-grow-1" style={{ minWidth: 0 }}>
              <h3 className="mb-0 text-truncate">{title}</h3>
              {tooltipText && (
                <div className="profileright-tooltip-wrapper">
                  <FontAwesomeIcon icon={faInfoCircle} size="sm" style={{ color: '#F35824' }} />
                  <div className="profileright-tooltip-box">
                    {tooltipText}
                  </div>
                </div>
              )}
            </Col>
            <Col xs="auto" className="text-end">
              <span
                onClick={() => inputRef.current.click()}
                style={{ color: "#F35824", cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              >
                <FontAwesomeIcon icon={faUpload} size="sm" />
              </span>
              <input
                type="file"
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={uploading}
              />
            </Col>
          </Row>


          {localFiles?.length === 0
            ? renderNoFilesMessage()
            : localFiles
              .slice(0, fileType !== 'ADD_INFO' ? (isExpanded ? localFiles.length : 2) : localFiles.length)
              .map((file, index) => {
                const originalName = file.originalName || file.name || 'Unnamed';
                const filePath = file.path || file.filePath || '';
                const truncatedName = truncateFileName(originalName);
                const iconType = getFileIcon(originalName);
                return (
                  <Row className={`mt-2 p-2 ${fileType === 'ADD_INFO' ? 'single-row-text' : ''}`} key={index}>
                    <Col lg={12}>
                      <div className="file-row d-flex align-items-center justify-content-between flex-wrap">
                        <div className="file-icon">
                          <FontAwesomeIcon icon={iconType} style={{ color: '#F35824' }} />

                        </div>
                        <div className="file-info flex-grow-1">
                          <div title={originalName} className="truncated-file-name">{truncatedName}</div>
                        </div>
                        <div className="download-icon">
                          <a href="#" onClick={(event) => { event.preventDefault(); downloadFile(filePath, originalName); }}>
                            <FontAwesomeIcon icon={faDownload} size='sm' style={{ color: '#F35824', cursor: 'pointer' }} />

                          </a>&nbsp;&nbsp;
                          <FontAwesomeIcon icon={faMinusCircle} size='sm' style={{ color: '#F35824', cursor: 'pointer' }} onClick={() => handleDeleteClick(file)} />
                        </div>
                      </div>
                    </Col>
                  </Row>
                );
              })}

          {fileType !== 'ADD_INFO' && localFiles.length > 2 && (
            <Row>
              <Col className="text-center">
                <button className="toggle-btn" onClick={toggleExpand}>
                  {isExpanded ? 'View Less' : 'View More'}
                </button>
              </Col>
            </Row>
          )}
        </Container>
      </Card.Body>
    </Card>
  );
};

export default CandidateFileContentsPanel;